<script setup>
// Tapetkalkylatorn på produktsidan: samma motor som rumsvisualiseringen, men utan konto.
//
// Kunden anger vägghöjd och längden på upp till fyra väggar, ser hur många rullar som behövs
// (hela våder per rulle, samma matematik som visualiseringen) och kan gå vidare till
// rumsvisualiseringen. Ingen sidladdning, ingen inloggning för själva uträkningen.
import { ref, computed, nextTick, onMounted, onBeforeUnmount } from "vue";
import { totalRolls } from "./lib/roll-math.js";
import { t } from "./lib/i18n.js";

const props = defineProps({
    title: { type: String, default: "" },
    handle: { type: String, default: "" },
    specs: { type: Object, default: null }, // { repeat, rollWidth, rollLength, patternMatch }
    visualizerUrl: { type: String, default: "" },
    visualizeBgs: { type: Array, default: () => [] }, // tapet-bakgrunder som roterar bakom knappen
    disclaimer: { type: String, default: "" }
});
const emit = defineEmits(["close"]);

// Vägghöjd delas av alla väggar (samma takhöjd i rummet), sedan längden per vägg.
const wallHeight = ref(null);
const wallLengths = ref([null, null, null, null]);

const panel = ref(null);
const closeBtn = ref(null);
let lastFocused = null;

const repeatDisplay = computed(() => {
    const r = props.specs && Number(props.specs.repeat);
    return r > 0 ? r : "";
});
const widthDisplay = computed(() => {
    const w = props.specs && Number(props.specs.rollWidth);
    return w > 0 ? w : "";
});

const rolls = computed(() => {
    const h = Number(wallHeight.value) || 0;
    const walls = wallLengths.value
        .map((w) => ({ width: Number(w) || 0, height: h }))
        .filter((wall) => wall.width > 0 && wall.height > 0);
    if (!walls.length) return 0;
    return totalRolls(walls, props.specs);
});

// ---- roterande bakgrund på visualiseringsknappen (samma crossfade som PDP-knappen) ----
const activeBg = ref(0);
let bgTimer = null;
const reduceMotion =
    typeof window !== "undefined" &&
    window.matchMedia &&
    window.matchMedia("(prefers-reduced-motion: reduce)").matches;

// ---- tillgänglighet: fokusfälla, Escape, återlämnat fokus ----
function focusables() {
    if (!panel.value) return [];
    return Array.from(
        panel.value.querySelectorAll(
            'a[href], button:not([disabled]), input:not([disabled]), select, textarea, [tabindex]:not([tabindex="-1"])'
        )
    ).filter((el) => el.offsetParent !== null || el === document.activeElement);
}
function onKeydown(e) {
    if (e.key === "Escape") {
        e.stopPropagation();
        emit("close");
        return;
    }
    if (e.key !== "Tab") return;
    const list = focusables();
    if (!list.length) return;
    const first = list[0];
    const last = list[list.length - 1];
    if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
    } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
    }
}

onMounted(() => {
    lastFocused = document.activeElement;
    document.addEventListener("keydown", onKeydown, true);
    document.documentElement.classList.add("bf2-wpcalc-lock");
    nextTick(() => closeBtn.value && closeBtn.value.focus());
    if (!reduceMotion && props.visualizeBgs.length > 1) {
        bgTimer = window.setInterval(() => {
            activeBg.value = (activeBg.value + 1) % props.visualizeBgs.length;
        }, 3400);
    }
});
onBeforeUnmount(() => {
    document.removeEventListener("keydown", onKeydown, true);
    document.documentElement.classList.remove("bf2-wpcalc-lock");
    if (bgTimer) window.clearInterval(bgTimer);
    // Lämna tillbaka fokus dit kunden kom ifrån (öppna-knappen).
    if (lastFocused && typeof lastFocused.focus === "function") lastFocused.focus();
});
</script>

<template>
    <!-- Teleporteras till body: produktsidans kolumner har sticky-positionering, och en
         position: fixed-overlay inuti en transformerad forfader far fel containing block. -->
    <Teleport to="body">
        <div class="bf2-wpcalc" role="dialog" aria-modal="true" aria-labelledby="bf2-wpcalc-title">
            <div class="bf2-wpcalc__overlay" @click="emit('close')"></div>
            <div ref="panel" class="bf2-wpcalc__panel">
                <div class="bf2-wpcalc__head">
                    <h2 id="bf2-wpcalc-title" class="bf2-heading bf2-wpcalc__title">{{ t("calculator.title") }}</h2>
                    <button
                        ref="closeBtn"
                        type="button"
                        class="bf2-wpcalc__close"
                        :aria-label="t('calculator.close_label')"
                        :title="t('calculator.close')"
                        @click="emit('close')"
                    >
                        <svg
                            viewBox="0 0 24 24"
                            width="18"
                            height="18"
                            fill="none"
                            stroke="currentColor"
                            stroke-width="1.4"
                            aria-hidden="true"
                        >
                            <path d="M6 6l12 12M18 6L6 18" stroke-linecap="round" />
                        </svg>
                    </button>
                </div>

                <div class="bf2-wpcalc__body">
                    <label class="bf2-wpcalc__field">
                        <span class="bf2-wpcalc__field-label">{{ t("calculator.wall_height") }}</span>
                        <span class="bf2-wpcalc__inputbox">
                            <input v-model="wallHeight" type="number" min="0" inputmode="numeric" />
                            <span class="bf2-wpcalc__unit">cm</span>
                        </span>
                    </label>

                    <label
                        v-for="(len, i) in wallLengths"
                        :key="i"
                        class="bf2-wpcalc__field"
                    >
                        <span class="bf2-wpcalc__field-label">{{ t("calculator.wall_length", { number: i + 1 }) }}</span>
                        <span class="bf2-wpcalc__inputbox">
                            <input
                                v-model="wallLengths[i]"
                                type="number"
                                min="0"
                                inputmode="numeric"
                            />
                            <span class="bf2-wpcalc__unit">cm</span>
                        </span>
                    </label>

                    <div class="bf2-wpcalc__field">
                        <span class="bf2-wpcalc__field-label">{{ t("calculator.pattern_repeat") }}</span>
                        <span class="bf2-wpcalc__inputbox bf2-wpcalc__inputbox--readonly">
                            <input :value="repeatDisplay" type="text" readonly tabindex="-1" />
                            <span class="bf2-wpcalc__unit">cm</span>
                        </span>
                    </div>

                    <div class="bf2-wpcalc__field">
                        <span class="bf2-wpcalc__field-label">{{ t("calculator.roll_width") }}</span>
                        <span class="bf2-wpcalc__inputbox bf2-wpcalc__inputbox--readonly">
                            <input :value="widthDisplay" type="text" readonly tabindex="-1" />
                            <span class="bf2-wpcalc__unit">cm</span>
                        </span>
                    </div>
                </div>

                <div class="bf2-wpcalc__result">
                    <p class="bf2-heading bf2-wpcalc__result-label">{{ t("calculator.rolls_needed") }}</p>
                    <p class="bf2-wpcalc__result-value" role="status">{{ rolls }}</p>
                </div>

                <div class="bf2-wpcalc__foot">
                    <p v-if="disclaimer" class="bf2-wpcalc__disclaimer">{{ disclaimer }}</p>

                    <a
                        v-if="visualizerUrl"
                        class="bf2-ai-cta bf2-ai-cta--tapet bf2-wpcalc__visualize"
                        :href="visualizerUrl"
                        data-bf-login-gate="wallpaper"
                    >
                        <span v-if="visualizeBgs.length" class="bf2-ai-cta__bg" aria-hidden="true">
                            <img
                                v-for="(src, i) in visualizeBgs"
                                :key="i"
                                class="bf2-ai-cta__img"
                                :class="{ 'is-active': i === activeBg }"
                                :src="src"
                                alt=""
                            />
                        </span>
                        <span class="bf2-ai-cta__scrim" aria-hidden="true"></span>
                        <span class="bf2-ai-cta__label">
                            {{ t("calculator.visualize") }}
                            <span class="bf2-ai-cta__arrow" aria-hidden="true">&rarr;</span>
                        </span>
                    </a>
                </div>
            </div>
        </div>
    </Teleport>
</template>
